import { useParams, Link } from "react-router-dom";
import { type Address, formatUnits } from "viem";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  TrendingUp,
  Users,
  BarChart3,
  Activity,
  Award,
  Loader2,
} from "lucide-react";
import { useTokenDataByAddress, useLaunchpadConfig } from "../../hooks/useLaunchpad";
import { TradePanel } from "./TradePanel";
import { TokenStatus, tokenStatusLabel, tokenStatusColor } from "../../config/tokens";
import {
  formatTokenAmount,
  formatJasmy,
  shortenAddress,
  timeAgo,
  explorerUrl,
  formatProgress,
  cn,
} from "../../lib/utils";
import toast from "react-hot-toast";

function PriceDisplay({ price }: { price: bigint | undefined }) {
  if (!price || price === 0n) {
    return (
      <div className="w-full h-[300px] rounded-card flex items-center justify-center bg-surface/20 border border-border/50">
        <div className="text-center">
          <Activity className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
          <p className="text-sm text-text-tertiary">No trade history yet</p>
          <p className="text-xs text-text-tertiary mt-1">Make the first trade to set the price</p>
        </div>
      </div>
    );
  }

  const priceNum = parseFloat(formatUnits(price, 18));
  const displayPrice = priceNum < 0.00000001
    ? priceNum.toExponential(4)
    : priceNum < 0.0001
      ? priceNum.toFixed(10)
      : priceNum.toFixed(8);

  return (
    <div className="w-full h-[300px] rounded-card flex items-center justify-center bg-surface/20 border border-border/50">
      <div className="text-center">
        <div className="text-4xl font-bold gradient-text mb-2">{displayPrice}</div>
        <div className="text-sm text-text-tertiary">JASMY per token</div>
        <div className="mt-4 text-xs text-text-tertiary">
          Historical chart requires an indexer service
        </div>
      </div>
    </div>
  );
}

export function TokenDashboard() {
  const { address } = useParams<{ address: string }>();
  const { token, stats, isLoading } = useTokenDataByAddress(
    address as Address | undefined
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20 text-center">
        <h2 className="font-display text-2xl text-text-secondary mb-4">
          Token Not Found
        </h2>
        <Link to="/launchpad" className="btn-primary inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Launchpad
        </Link>
      </div>
    );
  }

  const { graduationThreshold } = useLaunchpadConfig();
  const isGraduated = token.status === TokenStatus.Graduated;
  const progress = isGraduated ? 100 : stats ? formatProgress(stats.progress) : 0;

  function copyAddress() {
    navigator.clipboard.writeText(token!.tokenAddress);
    toast.success("Address copied");
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-20">
      <Link
        to="/launchpad"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Launchpad
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-card p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 rounded-card overflow-hidden bg-surface flex-shrink-0">
                {token.imageUri ? (
                  <img
                    src={token.imageUri}
                    alt={token.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-primary bg-primary/10">
                    {token.symbol.slice(0, 2)}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="font-display text-2xl font-bold text-text-primary">
                    {token.name}
                  </h1>
                  <span className="text-text-tertiary text-sm">${token.symbol}</span>
                  <span
                    className={cn(
                      "text-xs font-semibold px-2.5 py-1 rounded-full border",
                      token.status === TokenStatus.Live
                        ? "bg-primary/10 text-primary border-primary/20"
                        : token.status === TokenStatus.Graduated
                        ? "bg-secondary/10 text-secondary border-secondary/20"
                        : "bg-error/10 text-error border-error/20"
                    )}
                  >
                    {tokenStatusLabel(token.status)}
                  </span>
                </div>

                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <button
                    onClick={copyAddress}
                    className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
                  >
                    {shortenAddress(token.tokenAddress)}
                    <Copy className="w-3 h-3" />
                  </button>
                  <a
                    href={explorerUrl("token", token.tokenAddress)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors"
                  >
                    Explorer <ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-xs text-text-tertiary">
                    by {shortenAddress(token.creator)}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {timeAgo(token.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {token.description && (
              <p className="text-sm text-text-secondary mb-4">{token.description}</p>
            )}

            <div className="mb-2">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-text-secondary">Bonding Curve Progress</span>
                <span className="font-semibold text-text-primary">{progress.toFixed(2)}%</span>
              </div>
              <div className="w-full h-2.5 bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min(progress, 100)}%`,
                    background: "linear-gradient(90deg, #00d4aa, #7b61ff)",
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-xs text-text-tertiary">
                  {formatTokenAmount(token.reserveBalance)} / {formatTokenAmount(graduationThreshold)} JASMY
                </span>
                {isGraduated && (
                  <div className="flex items-center gap-1.5 text-xs text-secondary">
                    <Award className="w-3.5 h-3.5" />
                    Graduated to V2 LP
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="glass-card p-4">
            <h3 className="font-display font-semibold text-sm text-text-secondary mb-3 px-2">
              Current Price
            </h3>
            <PriceDisplay price={stats?.price} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <TrendingUp className="w-3.5 h-3.5" /> Price
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {stats ? formatTokenAmount(stats.price, 18, 8) : "--"} JASMY
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <BarChart3 className="w-3.5 h-3.5" /> Market Cap
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {stats ? formatTokenAmount(stats.marketCap) : "--"} JASMY
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <Activity className="w-3.5 h-3.5" /> Reserve
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {formatTokenAmount(token.reserveBalance)} JASMY
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <BarChart3 className="w-3.5 h-3.5" /> Volume
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {formatTokenAmount(token.tradingVolume)} JASMY
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <Users className="w-3.5 h-3.5" /> Holders
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {Number(token.holderCount)}
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
                <Activity className="w-3.5 h-3.5" /> Trades
              </div>
              <div className="font-semibold text-text-primary text-lg">
                {Number(token.tradeCount)}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-20">
            <TradePanel
              tokenId={token.id}
              tokenAddress={token.tokenAddress}
              tokenSymbol={token.symbol}
              isGraduated={isGraduated}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
